import { PREVIEW_SECONDS } from '@church/domain'
import { useSyncExternalStore } from 'react'

/**
 * One player for the whole app: a song's 30-second Apple Music preview, or
 * the church's own full recording of it (docs/15), wherever its play button
 * is — the song page, a list row, a plan's linked song, the change-match
 * panel. Starting one stops the other; every button showing that sound
 * shows the same progress. A full recording can also be scrubbed: its
 * player on the song page drags through it (`scrub`) and lets go (`seek`).
 *
 * The sound itself is the platform's: the web app mounts one component that
 * owns a single audio element and registers it here as the engine
 * (`registerPreviewEngine`), then reports what the element does
 * (`reportPreview`). Everything else — which preview is on, playing or
 * paused, how far — lives here, the same on every platform.
 */

export interface PreviewMeta {
  title: string
  artist: string
  /** Artwork for the system's media controls (a lock screen, a headset). */
  artworkUrl?: string | null
}

export interface PreviewEngine {
  /** Plays `url` from the start, or from `startAt` seconds in. */
  play(url: string, meta: PreviewMeta, startAt?: number): void
  pause(): void
  resume(): void
  stop(): void
  /** Moves the one playing to `seconds` in. */
  seek(seconds: number): void
}

export type PreviewStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error'

/** Apple's 30-second preview, or the church's own recording, played whole. */
export type PreviewSource = 'preview' | 'full'

export interface PreviewState {
  url: string | null
  status: PreviewStatus
  source: PreviewSource
  /** Seconds in. */
  position: number
  /** Seconds long (30 for a preview, the recording's own length for a full one, until the audio says). */
  duration: number
  /** Where a drag through the recording is, until it lets go; null otherwise. */
  scrubbing: number | null
}

/** How a sound plays: a preview by default; a full recording with its length when known. */
export interface PreviewOptions {
  full?: boolean
  /** The recording's length (from its upload), so the ring and scrubber know before the audio says. */
  durationSeconds?: number | null
}

const IDLE: PreviewState = {
  url: null,
  status: 'idle',
  source: 'preview',
  position: 0,
  duration: PREVIEW_SECONDS,
  scrubbing: null,
}

const startState = (
  url: string,
  options: PreviewOptions | undefined,
  position = 0,
): PreviewState => ({
  url,
  status: 'loading',
  source: options?.full ? 'full' : 'preview',
  position,
  duration: options?.full ? (options.durationSeconds ?? 0) : PREVIEW_SECONDS,
  scrubbing: null,
})

let state: PreviewState = IDLE
let engine: PreviewEngine | null = null
const listeners = new Set<() => void>()

function set(next: PreviewState) {
  state = next
  for (const listener of listeners) listener()
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The platform's player: the one place sound comes from. Returns its removal. */
export function registerPreviewEngine(next: PreviewEngine): () => void {
  engine = next
  return () => {
    if (engine === next) {
      engine = null
      set(IDLE)
    }
  }
}

/** What the platform's player did (loading, playing, time, the end, an error). */
export function reportPreview(
  update: Partial<Pick<PreviewState, 'status' | 'position' | 'duration'>> & { url: string },
) {
  // A late event from the preview before this one: not news.
  if (update.url !== state.url) return
  set({ ...state, ...update })
}

/** Plays `url` (stopping any other), or pauses / resumes it if it is the one on. */
export function togglePreview(url: string, meta: PreviewMeta, options?: PreviewOptions) {
  if (!engine) return
  if (state.url === url && (state.status === 'playing' || state.status === 'loading')) {
    engine.pause()
    set({ ...state, status: 'paused' })
    return
  }
  if (state.url === url && state.status === 'paused') {
    engine.resume()
    set({ ...state, status: 'playing' })
    return
  }
  set(startState(url, options))
  engine.play(url, meta)
}

/** A drag through the recording that is on: shown at once, heard when it lets go (`seekPreview`). */
export function scrubPreview(url: string, seconds: number) {
  if (state.url !== url) return
  set({ ...state, scrubbing: clampTo(seconds, state.duration) })
}

/**
 * Moves the recording to `seconds` in — or, when another sound (or none) is
 * on, starts this one there.
 */
export function seekPreview(
  url: string,
  seconds: number,
  meta: PreviewMeta,
  options?: PreviewOptions,
) {
  if (!engine) return
  if (state.url !== url) {
    const start = startState(url, options)
    const at = clampTo(seconds, start.duration)
    set({ ...start, position: at })
    engine.play(url, meta, at)
    return
  }
  const at = clampTo(seconds, state.duration)
  engine.seek(at)
  // Let go while paused, it stays paused there; an ended recording plays on from there.
  if (state.status === 'idle') {
    engine.resume()
    set({ ...state, status: 'loading', position: at, scrubbing: null })
    return
  }
  set({ ...state, position: at, scrubbing: null })
}

/** Within the recording (a length not known yet allows any point). */
const clampTo = (seconds: number, duration: number) =>
  Math.max(0, duration > 0 ? Math.min(duration, seconds) : seconds)

/** Stops whatever is playing (leaving the page that played it, say). */
export function stopPreview() {
  engine?.stop()
  set(IDLE)
}

/** The current preview, for tests and for the engine. */
export const previewState = () => state

/**
 * One play button's (or player's) view of the player: is it this sound,
 * playing, loading, how far through (0–1, and in seconds), the toggle, and
 * for a full recording the scrubber's two moves.
 */
export function usePreviewPlayer(url: string | null, meta: PreviewMeta, options?: PreviewOptions) {
  const current = useSyncExternalStore(subscribe, previewState, previewState)
  const mine = url !== null && current.url === url
  const status: PreviewStatus = mine ? current.status : 'idle'
  const full = options?.full === true
  const known = full ? (options?.durationSeconds ?? 0) : PREVIEW_SECONDS
  const duration = mine && current.duration > 0 ? current.duration : known
  const position = mine ? (current.scrubbing ?? current.position) : 0
  return {
    available: url !== null,
    full,
    status,
    playing: status === 'playing',
    loading: status === 'loading',
    progress: duration > 0 ? Math.min(1, Math.max(0, position / duration)) : 0,
    /** Seconds in, and long (0 while a recording's length is not known). */
    position,
    duration,
    /** Seconds left, for the button's label. */
    remaining: Math.max(0, Math.ceil(duration - position)),
    toggle: () => {
      if (url) togglePreview(url, meta, options)
    },
    /** While dragging: where the scrubber is (nothing heard yet). */
    scrub: (seconds: number) => {
      if (url) scrubPreview(url, seconds)
    },
    /** Letting go: the recording goes there (starting it if it wasn't on). */
    seek: (seconds: number) => {
      if (url) seekPreview(url, seconds, meta, options)
    },
  }
}
